import React, { useCallback, useMemo } from 'react';
import { SingleSelect } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { Typography } from '@fortress-technology-solutions/fortress-component-library/Atoms';

import useIntl from '../../../hooks/useIntl';
import messages from '../messages';
import usePermissionsHook from '../../../components/ElementWithPermissions/usePermissionsHook';

type BudgetActionsDropdownProps = {
  disabled: boolean,
  canEdit: boolean,
  currentForecast: any,
  onReview: () => void,
  onUtility: () => void,
  onLetter: () => void,
  onAuthorizeSignature: () => void,
  onApproveBudget: () => void,
  onBuildBudget: () => void,
  onReportHistory: () => void,
};

const BudgetActionsDropdown: React.FC<BudgetActionsDropdownProps> = ({
  disabled,
  canEdit,
  currentForecast,
  onReview,
  onUtility,
  onLetter,
  onAuthorizeSignature,
  onApproveBudget,
  onBuildBudget,
  onReportHistory,
}) => {
  const intl = useIntl();
  const hasAuthorizeSignaturePermission = usePermissionsHook({
    scope: ['rd-budget-signature-approval'],
  });

  const options = useMemo(
    () => [
      {
        value: 'review',
        text: intl.formatMessage(messages.reviewRDBudget),
      },
      {
        value: 'utility',
        text: intl.formatMessage(messages.openUtilityAllowanceTableButton),
      },
      {
        value: 'letter',
        text: intl.formatMessage(messages.residentLetter),
        disabled: !canEdit,
      },
      {
        value: 'authorizeSignature',
        text: intl.formatMessage(messages.authorizeSignatureBudgetAction),
        disabled: !canEdit || !hasAuthorizeSignaturePermission,
        tooltip: !hasAuthorizeSignaturePermission
          ? intl.formatMessage(messages.noPermissionAction)
          : null,
      },
      {
        value: 'approveBudget',
        text: intl.formatMessage(messages.approveBudgetAction),
        disabled: !canEdit,
        tooltip: currentForecast?.approvedFrom
          ? intl.formatMessage(messages.noApproveBudgetAlreadyAction)
          : null,
      },
      {
        value: 'buildBudget',
        text: intl.formatMessage(messages.buildBudgetAction),
        disabled: !canEdit,
      },
      {
        value: 'reportHistory',
        text: intl.formatMessage(messages.reportHistoryAction),
      },
    ],
    [intl, currentForecast, hasAuthorizeSignaturePermission],
  );

  const actionHandlers = useMemo(
    () => ({
      review: onReview,
      utility: onUtility,
      letter: onLetter,
      authorizeSignature: onAuthorizeSignature,
      approveBudget: onApproveBudget,
      buildBudget: onBuildBudget,
      reportHistory: onReportHistory,
    }),
    [],
  );

  const handleChange = useCallback(
    (newValue: string) => {
      const handler = actionHandlers[newValue];
      if (handler) {
        handler();
      }
    },
    [actionHandlers],
  );

  const renderValue = useCallback(
    () => (
      <Typography color="text.primary">
        {intl.formatMessage(messages.budgetActions)}
      </Typography>
    ),
    [intl],
  );

  return (
    <SingleSelect
      name="budgetActions"
      options={options}
      value=""
      onChange={handleChange}
      disabled={disabled}
      displayEmpty
      renderValue={renderValue}
      HelperTextProps={{ sx: { display: 'none' } }}
      sx={{
        minWidth: 'max-content',
        textTransform: 'none',
      }}
    />
  );
};

export default BudgetActionsDropdown;
